/**
 * This file was auto-generated
 * Do not edit manually
 */

import { StackBehaviourSubject, useGetSubject } from '@stack/react-shared'
import { createContext, useContext, useState, useMemo, type ReactNode, useEffect, useCallback } from 'react'

import { keys, locales } from './i18n'
import type { Translations, Locales } from './i18n-types'
import { isLocale } from './i18n-util'

interface I18nContextValue {
	locale: Locales
	L: Translations
	setLocale: (locale: Locales) => void
	resetToDefault: () => void
}

const I18nContext = createContext<I18nContextValue | null>(null)

const sanitizeLocale = (locale: string | undefined | null): Locales | undefined => {
	locale = locale?.toLowerCase()
	if (locale && isLocale(locale)) {
		return locale
	}
	return undefined
}

const getInitialLocale = (
	forcedInitialLocale: Locales | null | string | undefined,
	savedLocale: Locales | null | string | undefined,
	defaultLocale: Locales | null | string | undefined
): Locales => {
	const defaultLocaleSanitized = sanitizeLocale(defaultLocale) ?? 'en'
	const savedLocaleSanitized = sanitizeLocale(savedLocale) ?? defaultLocaleSanitized
	const forcedInitialLocaleSanitized = sanitizeLocale(forcedInitialLocale)

	if (forcedInitialLocaleSanitized) {
		return forcedInitialLocaleSanitized
	}
	if (savedLocaleSanitized) {
		return savedLocaleSanitized
	} else {
		return defaultLocaleSanitized
	}
}

let _currentLocale: Locales | undefined = undefined
let _showTranslationKeys = false
export const getCurrentLocale = () => (_showTranslationKeys ? keys : locales[_currentLocale ?? 'en'])

export function I18nProvider({
	children,
	locale$,
	defaultLocales,
	forcedInitialLocale,
	showTranslationKeys = false
}: {
	children: ReactNode
	locale$: StackBehaviourSubject<Locales | undefined>
	defaultLocales: (Locales | undefined | null | string)[]
	forcedInitialLocale?: Locales | null | string
	/** Render each string as its dotted translation key. Not a selectable locale. */
	showTranslationKeys?: boolean
}) {
	const defaultLocale =
		sanitizeLocale(defaultLocales.map(x => x?.toLowerCase()).filter(x => x && isLocale(x))[0]) ?? 'en'

	const [locale, setLocaleState] = useState<Locales>(
		getInitialLocale(forcedInitialLocale, locale$.value, defaultLocale)
	)

	_currentLocale = locale
	_showTranslationKeys = showTranslationKeys

	const setLocale = useCallback(
		(locale: Locales) => {
			const next = sanitizeLocale(locale) ?? defaultLocale
			locale$.next(next)
			_currentLocale = next
			setLocaleState(next)
		},
		[defaultLocale]
	)

	const savedLocale = sanitizeLocale(useGetSubject(locale$))
	if (savedLocale && savedLocale !== locale) {
		setLocale(savedLocale)
	}

	const resetToDefault = useCallback(() => {
		locale$.next(defaultLocale)
	}, [defaultLocale])

	const value = useMemo(
		() => ({
			locale,
			L: showTranslationKeys ? keys : locales[locale],
			setLocale,
			resetToDefault
		}),
		[locale, showTranslationKeys]
	)

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
	const context = useContext(I18nContext)
	if (!context) {
		throw new Error('useI18n must be used within I18nProvider')
	}
	return context
}

export function useTranslations(): Translations {
	return useI18n().L
}
